import React from 'react';
import { api, isSignInExpired } from '../lib/api';
import { formatInTz } from '../lib/timezone';
import { useViewerTz } from '../hooks/useViewerTz';
import { OUTCOME_NEXT_STEP, formatPesos, isOpenOutcome, outcomeLabel, referenceFromHash } from '../lib/payments';
import { Badge } from '../components';
import { TestPaymentsNote } from '../components/TestPaymentsNote';
import { palette as T, type } from '../design';

// The result page (JTP-20): a student back from JTPay's pay page reads where their booking payment
// stands and the amount, with one next step. main.jsx hands /pay/result?reference=<ref> to
// #/pay-result?reference=<ref>. The page only reads: bookings change on JTPay's signed notice.

const FONT = "'Plus Jakarta Sans', sans-serif";
const HEADING = { fontFamily: "'Lora', serif", fontWeight: 700, fontSize: 24, color: T.espresso, margin: '0 0 12px' };
const BODY = { fontFamily: FONT, fontSize: 15, color: T.walnut, lineHeight: 1.6, margin: '0 0 20px' };
const PRIMARY = {
  background: T.terracotta, color: T.white, border: 'none', borderRadius: 12, padding: '12px 24px',
  fontFamily: FONT, fontWeight: 600, fontSize: type.body, cursor: 'pointer', textDecoration: 'none', display: 'inline-block',
};
const SECONDARY = { ...PRIMARY, background: 'transparent', color: T.terracotta, border: '2px solid ' + T.terracotta, padding: '10px 22px' };

// The student's bookings, on their own tab of the dashboard.
const BOOKINGS_HASH = '#/dashboard-student?tab=bookings';

const NOT_FOUND_MESSAGE = 'We could not find this payment.';
const LOAD_FAILED_MESSAGE = 'We could not load this payment. Check again in a minute.';

const OUTCOME_BADGE = { paid: 'sage', overpaid: 'sage', paid_multiple: 'sage', waiting: 'gold', confirming: 'gold', underpaid: 'terracotta' };
const STATUS_WORD = { pending: 'Pending', confirmed: 'Confirmed', cancelled: 'Cancelled', completed: 'Completed' };

export function PayResultPage() {
  const [reference] = React.useState(() => referenceFromHash(window.location.hash));
  const [viewerTz] = useViewerTz();
  // loading | ready | not-found | failed
  const [state, setState] = React.useState(reference ? 'loading' : 'not-found');
  const [payment, setPayment] = React.useState(null);
  const [checking, setChecking] = React.useState(false);

  const load = React.useCallback(async () => {
    if (!reference) return;
    setChecking(true);
    try {
      const data = await api.get(`/booking-payments/${encodeURIComponent(reference)}`);
      setPayment(data?.bookingPayment || null);
      setState(data?.bookingPayment ? 'ready' : 'failed');
    } catch (err) {
      // An ended sign-in already took them to the login page; signing in brings them back here.
      if (isSignInExpired(err)) return;
      if (err?.status === 404) {
        setState('not-found');
      } else {
        console.error('[PayResultPage] loading the booking payment failed', err);
        setState('failed');
      }
    } finally {
      setChecking(false);
    }
  }, [reference]);

  React.useEffect(() => { load(); }, [load]);

  const amount = payment ? formatPesos(payment.amount) : null;

  return (
    <div style={{ minHeight: '70vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '96px 16px 48px', background: T.ivory }}>
      <div data-testid="pay-result" style={{ width: '100%', maxWidth: 520, background: T.white, borderRadius: 20, border: '1px solid ' + T.border, padding: '32px 24px', boxSizing: 'border-box' }}>
        {state === 'loading' && (
          <p role="status" style={{ ...BODY, margin: 0, textAlign: 'center' }}>Loading your payment…</p>
        )}

        {state === 'not-found' && (
          <div style={{ textAlign: 'center' }}>
            <h1 style={HEADING}>{NOT_FOUND_MESSAGE}</h1>
            <p style={BODY}>Open your bookings to see where they stand.</p>
            <a href={BOOKINGS_HASH} style={PRIMARY}>Go to my bookings</a>
          </div>
        )}

        {state === 'failed' && (
          <div style={{ textAlign: 'center' }}>
            <h1 style={HEADING}>Booking payment</h1>
            <p role="alert" style={{ ...BODY, color: T.errorRed }}>{LOAD_FAILED_MESSAGE}</p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
              <button onClick={load} disabled={checking} style={PRIMARY}>{checking ? 'Checking…' : 'Check again'}</button>
              <a href={BOOKINGS_HASH} style={SECONDARY}>Go to my bookings</a>
            </div>
          </div>
        )}

        {state === 'ready' && payment && (
          <div>
            <p style={{ fontFamily: FONT, fontSize: type.label, fontWeight: 600, color: T.driftwood, textTransform: 'uppercase', letterSpacing: 0.8, margin: '0 0 8px' }}>Booking payment</p>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', margin: '0 0 8px' }}>
              <h1 data-testid="pay-result-outcome" style={{ ...HEADING, margin: 0 }}>{outcomeLabel(payment.outcome)}</h1>
              <Badge variant={OUTCOME_BADGE[payment.outcome] || 'subtle'}>{outcomeLabel(payment.outcome)}</Badge>
            </div>
            <TestPaymentsNote style={{ margin: '0 0 8px' }} />
            {amount && (
              <p data-testid="pay-result-amount" style={{ fontFamily: "'Lora', serif", fontWeight: 700, fontSize: type.h2, color: T.espresso, margin: '0 0 4px' }}>{amount}</p>
            )}
            <p style={{ fontFamily: FONT, fontSize: type.label, color: T.driftwood, margin: '0 0 20px', wordBreak: 'break-all' }}>Reference {payment.reference}</p>

            <p style={BODY}>{OUTCOME_NEXT_STEP[payment.outcome] || 'Open your bookings to see where they stand.'}</p>

            {Array.isArray(payment.bookings) && payment.bookings.length > 0 && (
              <div style={{ background: T.ivory, borderRadius: 14, border: '1px solid ' + T.border, padding: '10px 16px', margin: '0 0 20px' }}>
                <p style={{ fontFamily: FONT, fontWeight: 700, fontSize: type.label, color: T.espresso, margin: '4px 0 6px' }}>
                  {payment.bookings.length === 1 ? 'This payment covers 1 booking' : `This payment covers ${payment.bookings.length} bookings`}
                </p>
                {payment.bookings.map((b) => (
                  <div key={b.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '6px 0', borderTop: '1px solid ' + T.border, flexWrap: 'wrap' }}>
                    <span style={{ fontFamily: FONT, fontSize: type.body, color: T.espresso, minWidth: 0 }}>
                      {b.title || 'Session'} · {formatInTz(b.startAt, viewerTz, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                    </span>
                    <span style={{ fontFamily: FONT, fontSize: type.label, fontWeight: 600, color: T.walnut }}>{STATUS_WORD[b.status] || b.status}</span>
                  </div>
                ))}
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <a href={BOOKINGS_HASH} style={PRIMARY}>Go to my bookings</a>
              {isOpenOutcome(payment.outcome) && (
                <button onClick={load} disabled={checking} style={SECONDARY}>{checking ? 'Checking…' : 'Check again'}</button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
